import { useListSecrets } from "@/lib/hooks/useSecrets";
import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  initialSecretState,
  secretReducer,
  secretSelectors,
} from "./secretReducer";
import type { Secret, SecretState } from "./types";
import { useSecretOperations } from "./useSecretOperations";
import {
  canSaveSecret,
  createEmptySecret,
  createOperationKey,
  mergeSecretsWithServerData,
} from "./utils";

// Helper function to check if server keys arrays are equal
const areServerKeysEqual = (keys1: string[], keys2: string[]): boolean => {
  if (keys1.length !== keys2.length) {
    return false;
  }
  return keys1.every((key, index) => key === keys2[index]);
};

interface UseSecretStateProps {
  projectId: string;
}

interface UseSecretStateReturn {
  // State
  state: SecretState;

  // Server data
  existingSecretKeys: string[];
  isLoadingSecrets: boolean;
  secretsError: unknown;

  // Operations
  saveSecret: (index: number) => Promise<void>;
  deleteSecret: (index: number, key: string) => Promise<void>;
  addNewSecret: () => void;
  removeSecret: (index: number) => void;
  updateSecret: (index: number, updates: Partial<Secret>) => void;

  // UI actions
  startEditing: (key: string) => void;
  stopEditing: (key: string) => void;
  toggleVisibility: (key: string) => void;
  clearError: (key: string) => void;

  // Validation
  canSaveSecret: (index: number) => boolean;

  // Operations status
  isOperationInProgress: boolean;
}

export function useSecretState({
  projectId,
}: UseSecretStateProps): UseSecretStateReturn {
  const [state, dispatch] = useReducer(secretReducer, initialSecretState);
  const initialLoadRef = useRef(true);
  const previousServerKeysRef = useRef<string[]>([]);
  const pendingOperations = useRef<Set<string>>(new Set());

  // Use ref to store current state to avoid circular dependency in useEffect
  const stateRef = useRef(state);
  stateRef.current = state;

  // Get server data
  const {
    data: secretsResponse,
    isLoading: isLoadingSecrets,
    error: secretsError,
  } = useListSecrets(projectId);
  const existingSecretKeys = secretsResponse?.data.secretKeys || [];

  // Get operations hook
  const {
    saveSecret: saveSecretOperation,
    deleteSecret: deleteSecretOperation,
    isLoading: isOperationLoading,
  } = useSecretOperations({ projectId });

  // Sync server data with local state
  useEffect(() => {
    // Check if server data has actually changed
    const serverKeysChanged =
      initialLoadRef.current ||
      !areServerKeysEqual(previousServerKeysRef.current, existingSecretKeys);

    if (serverKeysChanged) {
      const mergedSecrets = mergeSecretsWithServerData(
        stateRef.current.secrets,
        existingSecretKeys,
      );

      dispatch({ type: "SET_SECRETS", secrets: mergedSecrets });
      previousServerKeysRef.current = [...existingSecretKeys];
      initialLoadRef.current = false;
    }
  }, [existingSecretKeys]); // Now only depends on server data, avoiding circular dependency

  // Remove debounced save - we'll only use explicit saves

  // Operations
  const saveSecret = useCallback(
    async (index: number): Promise<void> => {
      const secret = secretSelectors.getSecretByIndex(state, index);
      if (!secret || !canSaveSecret(secret, state.secrets, index)) {
        return;
      }

      const operationKey = createOperationKey(secret.key, "save");

      if (pendingOperations.current.has(operationKey)) {
        return; // Already in progress
      }

      pendingOperations.current.add(operationKey);
      dispatch({ type: "START_OPERATION", key: secret.key, operation: "save" });

      try {
        await saveSecretOperation(secret.key, secret.value);
        dispatch({
          type: "COMPLETE_OPERATION",
          key: secret.key,
          operation: "save",
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save secret";
        dispatch({
          type: "FAIL_OPERATION",
          key: secret.key,
          operation: "save",
          error: errorMessage,
        });
      } finally {
        pendingOperations.current.delete(operationKey);
      }
    },
    [state, saveSecretOperation],
  );

  const deleteSecret = useCallback(
    async (index: number, key: string): Promise<void> => {
      const operationKey = createOperationKey(key, "delete");

      if (pendingOperations.current.has(operationKey)) {
        return; // Already in progress
      }

      pendingOperations.current.add(operationKey);
      dispatch({ type: "START_OPERATION", key, operation: "delete" });

      try {
        await deleteSecretOperation(key);
        dispatch({ type: "COMPLETE_OPERATION", key, operation: "delete" });
        // Remove from local state after successful deletion
        dispatch({ type: "REMOVE_SECRET", index });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to delete secret";
        dispatch({
          type: "FAIL_OPERATION",
          key,
          operation: "delete",
          error: errorMessage,
        });
      } finally {
        pendingOperations.current.delete(operationKey);
      }
    },
    [deleteSecretOperation],
  );

  const addNewSecret = useCallback(async () => {
    // First, save any valid new secrets that haven't been saved yet
    const newSecretsToSave = state.secrets
      .map((secret, index) => ({ secret, index }))
      .filter(
        ({ secret, index }) =>
          !secret.isExisting &&
          canSaveSecret(secret, state.secrets, index) &&
          !secretSelectors.isLoading(state, secret.key),
      );

    // Save all valid new secrets in parallel
    await Promise.all(newSecretsToSave.map(({ index }) => saveSecret(index)));

    // Check if the last field has an empty key
    if (state.secrets.length > 0) {
      const lastSecret = state.secrets[state.secrets.length - 1];
      if (!lastSecret?.key?.trim()) {
        return;
      }
    }

    const newSecret = createEmptySecret();

    dispatch({ type: "ADD_SECRET", secret: newSecret });

    // Auto-start editing the new secret
    setTimeout(() => {
      dispatch({ type: "START_EDIT", key: "" });
    }, 0);
  }, [state, saveSecret]);

  const removeSecret = useCallback((index: number) => {
    dispatch({ type: "REMOVE_SECRET", index });
  }, []);

  const updateSecret = useCallback(
    (index: number, updates: Partial<Secret>) => {
      dispatch({ type: "UPDATE_SECRET", index, updates });
      // No auto-save on update - only explicit saves
    },
    [],
  );

  // UI actions
  const startEditing = useCallback((key: string) => {
    dispatch({ type: "START_EDIT", key });
  }, []);

  const stopEditing = useCallback((key: string) => {
    dispatch({ type: "STOP_EDIT", key });
  }, []);

  const toggleVisibility = useCallback((key: string) => {
    dispatch({ type: "TOGGLE_VISIBILITY", key });
  }, []);

  const clearError = useCallback((key: string) => {
    dispatch({ type: "CLEAR_ERROR", key });
  }, []);

  // Validation
  const canSaveSecretAtIndex = useCallback(
    (index: number): boolean => {
      const secret = secretSelectors.getSecretByIndex(state, index);
      if (!secret) {
        return false;
      }
      return canSaveSecret(secret, state.secrets, index);
    },
    [state],
  );

  const isOperationInProgress =
    isOperationLoading ||
    secretSelectors.hasAnyPendingOperations(state) ||
    pendingOperations.current.size > 0;

  return {
    // State
    state,

    // Server data
    existingSecretKeys,
    isLoadingSecrets,
    secretsError,

    // Operations
    saveSecret,
    deleteSecret,
    addNewSecret,
    removeSecret,
    updateSecret,

    // UI actions
    startEditing,
    stopEditing,
    toggleVisibility,
    clearError,

    // Validation
    canSaveSecret: canSaveSecretAtIndex,

    // Operations status
    isOperationInProgress,
  };
}
